import { Button, FilterButton, HorizontalProgressBar, Legend } from '@vake/ui-kit';
import { Feature } from 'geojson';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ViewState } from 'react-map-gl';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useGetReportInfo } from '../../../requests/actor-mode';
import {
    useAcqusitions,
    useGetFootprints,
    useGetObjects,
    useMetadataQuery,
    useSendToMattermost,
} from '../../../requests/reports';
import { RMap2 } from '../../../shared/components/overwatch-map-v2';
import { allTagsActor, elevatedTags } from '../../../shared/constants';
import { LeftBar } from '../components/info-left/info-left';
import { OverwatchRight2 } from '../components/overwatch-right-2';
import { OverwatchContext } from './overwatch-2-context';
import { initialMapView } from './overwatch-2-initial-state';
import { Filters, Settings, View } from './overwatch-2-types';
import { legendData } from './reportInfo/legend-data';

// const TopChildren = () => {
//     return (
//         <div className="px-2">
//             <Select
//                 selectedValue={selectedView}
//                 onChange={(value) => setSelectedView(value as 'map' | 'table')}
//                 options={[
//                     { value: 'map', icon: <GlobeHemisphereEast size={16} /> },
//                     { value: 'table', icon: <Boat size={16} /> },
//                 ]}
//             />
//         </div>
//     );
// };
const TopChildren = ({ url }: { url: string }) => {
    const { mutate } = useSendToMattermost();
    // This components is temporarily used to link to actor mode from overwatch report until full table view is implemented
    return (
        <Link to={url}>
            <Button
                text="Go To Table"
                size="xsmall"
                variant="neutral"
                onClick={() => {
                    mutate(`User clicked Go to Table from this [Overwatch 2.0](${window.location.href})`);
                }}
            />
        </Link>
    );
};

const searchMatches = (properties: unknown, query: string): boolean => {
    if (!query) return true;
    const lowerQuery = query.toLowerCase();
    return (
        properties?.name?.toLowerCase().includes(lowerQuery) ||
        properties?.mmsi?.toString().includes(lowerQuery) ||
        properties?.imo?.toString().includes(lowerQuery)
    );
};
const tagFiltering = (properties: unknown, tags: FilterButton[], tagFilterStrategy: 'or' | 'and'): boolean => {
    const activeTags = tags.filter((tag) => !tag.subtle);
    if (activeTags.length === 0) {
        return true;
    }
    const selectedTags = activeTags.map((tag) => tag.children as string);
    if (tagFilterStrategy === 'or') {
        return selectedTags.some((tag) => properties?.tags?.includes(tag));
    } else if (tagFilterStrategy === 'and') {
        return selectedTags.every((tag) => properties?.tags?.includes(tag));
    }
    return true;
};
const imageSourceFiltering = (properties: unknown, activeSources: ImageSource[]): boolean => {
    if (activeSources.length === 0) {
        return true;
    }
    return activeSources.includes(properties?.image_source);
};

type RouteParams = {
    reportUuid: string;
};
const Overwatch = () => {
    const params = useParams<RouteParams>();
    const [searchParams, setSearchParams] = useSearchParams();
    const reportUuid = params.reportUuid || '';

    // Filters
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [verifiedActive, setVerifiedActive] = useState<boolean>(false);
    const [darkActive, setDarkActive] = useState<boolean>(false);
    const [unCorrelatedActive, setUnCorrelatedActive] = useState<boolean>(false);
    const [platformsActive, setPlatformsActive] = useState<boolean>(false);
    const [windmillsActive, setWindmillsActive] = useState<boolean>(false);
    const [tagFilterStrategy, setTagFilterStrategy] = useState<'or' | 'and'>('or');
    const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
    const [hoveredFeature, setHoveredFeature] = useState<Feature | null>(null);
    const [query, setQuery] = useState<string>('');
    const defaultConfidence = 92; // This will change based on production model
    const [darkDetectionConfidence, setDarkDetectionConfidence] = useState<number>(defaultConfidence);
    const noneActive = !darkActive && !unCorrelatedActive && !verifiedActive && !windmillsActive && !platformsActive;
    const [openDate, setOpenDate] = useState<boolean>(true);
    const [openConfidence, setOpenConfidence] = useState<boolean>(true);
    const [openDetectionType, setOpenDetectionType] = useState<boolean>(true);
    const [openStaticObjects, setOpenStaticObjects] = useState<boolean>(false);
    const [openTags, setOpenTags] = useState<boolean>(false);
    const [isAnythingLoading, setIsAnythingLoading] = useState<boolean>(true);
    const [loadingProgress, setLoadingProgress] = useState<number>(0);
    const [insightUuid, setInsightUuid] = useState<string | null>(null);
    const [bookmarkedInsights, setBookmarkedInsights] = useState<string[]>([]);
    const [viewedInsights, setViewedInsights] = useState<string[]>([]);
    const [openImageSources, setOpenImageSources] = useState<boolean>(false);
    const [sentinel1Active, setSentinel1Active] = useState<boolean>(false);
    const [sentinel2Active, setSentinel2Active] = useState<boolean>(false);
    const noSourceActive = !sentinel1Active && !sentinel2Active;
    // Settings
    const [showProcessingStatus, setShowProcessingStatus] = useLocalStorage<boolean>('showProcessingStatus', false);
    const [showSatelliteImage, setShowSatelliteImage] = useLocalStorage<boolean>('showSatelliteImage', false);
    const [showEEZ, setShowEEZ] = useLocalStorage<boolean>('showEEZ', false);
    // View
    const [selected, setSelected] = useState<number>(0);
    const [selectedView, setSelectedView] = useState<'map' | 'table'>('map');
    // Map View
    const [mapView, setMapView] = useState<ViewState>(initialMapView);
    // Data Fetching
    const {
        data: report,
        isError: isErrorReport,
        isLoading: isLoadingReport,
        isSuccess: isSuccessReport,
    } = useGetReportInfo(reportUuid);
    const { data: darkVessels, isLoading: isLoadingDarkVessels } = useGetObjects(reportUuid, startDate, 'dark_vessel');
    const { data: matchedVessels, isLoading: isLoadingMatchedVessels } = useGetObjects(
        reportUuid,
        startDate,
        'matched'
    );
    const { data: unDetectedVessels, isLoading: isLoadingUnDetectedVessels } = useGetObjects(
        reportUuid,
        startDate,
        'undetected'
    );
    const { data: windmills, isLoading: isLoadingWindmills } = useGetObjects(reportUuid, startDate, 'windmill');
    const { data: platforms, isLoading: isLoadingPlatforms } = useGetObjects(reportUuid, startDate, 'platform');
    const { data: metaData, isSuccess: isMetadataSuccess, isLoading: isLoadingMetaData } = useMetadataQuery(reportUuid);
    const { data: footprintsSentinel1, isLoading: isLoadingFootprintsSentinel1 } = useGetFootprints(
        reportUuid,
        startDate,
        showProcessingStatus,
        'sentinel1',
        sentinel1Active || noSourceActive
    );
    const { data: footprintsSentinel2, isLoading: isLoadingFootprintsSentinel2 } = useGetFootprints(
        reportUuid,
        startDate,
        showProcessingStatus,
        'sentinel2',
        sentinel2Active || noSourceActive
    );
    const { data: acquisitions, isLoading: isLoadingAcquisition } = useAcqusitions(reportUuid, startDate || undefined);
    const [tags, setTags] = useState<FilterButton[]>(
        allTagsActor
            .map((tag) => ({
                children: tag,
                variant: elevatedTags.includes(tag) ? ('warning' as const) : ('neutral' as const),
                size: 'small' as const,
                subtle: true,
            }))
            // Sorts elevatedTags before regular
            .sort((a, b) => b.variant.localeCompare(a.variant))
    );
    useEffect(() => {
        let title = 'Overwatch';
        if (metaData && metaData.metadata && metaData.metadata.name) {
            title = metaData.metadata.name;
            if (!(searchParams.has('latitude') && searchParams.has('longitude'))) {
                setMapView((prev) => ({
                    ...prev,
                    longitude: metaData.metadata.center_pos[0],
                    latitude: metaData.metadata.center_pos[1],
                }));
            }
        }
        document.title = title;
    }, [isMetadataSuccess, metaData, searchParams]);
    const findFeatureFromUuid = useCallback(
        (uuid: string) => {
            return (
                [
                    ...(matchedVessels?.features ?? []),
                    ...(darkVessels?.features ?? []),
                    ...(unDetectedVessels?.features ?? []),
                    ...(platforms?.features ?? []),
                    ...(windmills?.features ?? []),
                ].find((feature) => feature.properties?.uuid === uuid) || null
            );
        },
        [matchedVessels, darkVessels, unDetectedVessels, platforms, windmills]
    );
    useEffect(() => {
        const spStartDate = searchParams.get('startDate');
        if (spStartDate) {
            setStartDate(new Date(spStartDate));
        }
        const confidence = searchParams.get('confidence');
        if (confidence) {
            setDarkDetectionConfidence(Number(confidence));
        }
        const uuid = searchParams.get('uuid');
        if (uuid) {
            setInsightUuid(uuid);
        }
        const lat = searchParams.get('latitude');
        const lon = searchParams.get('longitude');
        if (lat && lon) {
            const latNum = Number(lat);
            const lonNum = Number(lon);
            if (!isNaN(latNum) && !isNaN(lonNum)) {
                setMapView((prev) => ({ ...prev, latitude: latNum, longitude: lonNum }));
            }
        }
    }, [searchParams]);
    const setStartDateAndUrl = (date: Date | null) => {
        setStartDate(date);
        if (date) {
            const startDateString = date.toISOString().split('T')[0];
            const newParams = new URLSearchParams(searchParams);
            newParams.set('startDate', startDateString);
            setSearchParams(newParams);
        }
    };
    const setDarkDetectionConfidenceAndUrl = (confidence: number) => {
        setDarkDetectionConfidence(confidence);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('confidence', confidence.toString());
        setSearchParams(newParams);
    };
    const setSelectedFeatureAndUrl = (selectedFeature: Feature | null) => {
        setSelectedFeature(selectedFeature);
        setViewedInsights((prevItems) => {
            if (selectedFeature?.properties?.uuid && !prevItems.includes(selectedFeature?.properties?.uuid)) {
                return [...prevItems, selectedFeature?.properties?.uuid];
            } else {
                return prevItems;
            }
        });
        if (selectedFeature && selectedFeature.properties?.uuid) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('uuid', selectedFeature.properties.uuid);
            setSearchParams(newParams);
        } else {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('uuid');
            setSearchParams(newParams);
        }
    };
    useEffect(() => {
        const loadingBooleans = [
            isLoadingReport,
            isLoadingFootprintsSentinel1,
            isLoadingFootprintsSentinel2,
            isLoadingDarkVessels,
            isLoadingMatchedVessels,
            isLoadingUnDetectedVessels,
            isLoadingWindmills,
            isLoadingPlatforms,
            isLoadingMetaData,
            isLoadingAcquisition,
        ];
        const isLoadingCount = loadingBooleans.reduce((count, value) => {
            return count + (value ? 1 : 0);
        }, 0);
        setIsAnythingLoading(isLoadingCount > 0);
        setLoadingProgress(((loadingBooleans.length - isLoadingCount) / loadingBooleans.length) * 100);
        if (isLoadingCount === 0 && insightUuid) {
            const feature = findFeatureFromUuid(insightUuid);
            if (feature) {
                setSelectedFeature(feature);
            }
            setInsightUuid(null);
        }
    }, [
        isLoadingReport,
        isLoadingDarkVessels,
        isLoadingMatchedVessels,
        isLoadingUnDetectedVessels,
        isLoadingWindmills,
        isLoadingPlatforms,
        isLoadingMetaData,
        insightUuid,
        isLoadingAcquisition,
        findFeatureFromUuid,
        isLoadingFootprintsSentinel1,
        isLoadingFootprintsSentinel2,
    ]);
    const activeImageSources = useMemo(() => {
        const sources: ImageSource[] = [];
        if (sentinel1Active) {
            sources.push('sentinel1');
        }
        if (sentinel2Active) {
            sources.push('sentinel2');
        }
        return sources;
    }, [sentinel1Active, sentinel2Active]);
    const darkVesselsFiltered = useMemo(() => {
        if (!!darkVessels && (noneActive || darkActive)) {
            return {
                ...darkVessels,
                features: darkVessels.features
                    .filter(
                        (feature) =>
                            feature.properties?.object_class_score >= darkDetectionConfidence / 100 && query === ''
                    )
                    .filter((feature) => tagFiltering(feature.properties, tags, tagFilterStrategy))
                    .filter((feature) => imageSourceFiltering(feature.properties, activeImageSources)),
            };
        }
        return { type: 'FeatureCollection' as const, features: [] };
    }, [
        darkVessels,
        noneActive,
        darkActive,
        darkDetectionConfidence,
        query,
        tags,
        tagFilterStrategy,
        activeImageSources,
    ]);
    const matchedVesselsFiltered = useMemo(() => {
        if (!!matchedVessels && (noneActive || verifiedActive)) {
            return {
                ...matchedVessels,
                features: matchedVessels.features
                    .filter((feature) => searchMatches(feature.properties, query))
                    .filter((feature) => tagFiltering(feature.properties, tags, tagFilterStrategy))
                    .filter((feature) => imageSourceFiltering(feature.properties, activeImageSources)),
            };
        }
        return { type: 'FeatureCollection' as const, features: [] };
    }, [matchedVessels, noneActive, verifiedActive, query, tags, tagFilterStrategy, activeImageSources]);
    const unDetectedVesselsFiltered = useMemo(() => {
        if (!!unDetectedVessels && (noneActive || unCorrelatedActive)) {
            return {
                ...unDetectedVessels,
                features: unDetectedVessels.features
                    .filter((feature) => searchMatches(feature.properties, query))
                    .filter((feature) => tagFiltering(feature.properties, tags, tagFilterStrategy))
                    .filter((feature) => imageSourceFiltering(feature.properties, activeImageSources)),
            };
        }
        return { type: 'FeatureCollection' as const, features: [] };
    }, [unDetectedVessels, noneActive, unCorrelatedActive, query, tags, tagFilterStrategy, activeImageSources]);
    const windmillsFiltered = useMemo(() => {
        if (!!windmills && (noneActive || windmillsActive)) {
            return {
                ...windmills,
                features: windmills.features
                    .filter((feature) => searchMatches(feature.properties, query))
                    .filter((feature) => tagFiltering(feature.properties, tags, tagFilterStrategy))
                    .filter((feature) => imageSourceFiltering(feature.properties, activeImageSources)),
            };
        }
        return { type: 'FeatureCollection' as const, features: [] };
    }, [noneActive, query, tags, windmills, windmillsActive, tagFilterStrategy, activeImageSources]);
    const platformsFiltered = useMemo(() => {
        if (!!platforms && (noneActive || platformsActive)) {
            return {
                ...platforms,
                features: platforms.features
                    .filter((feature) => searchMatches(feature.properties, query))
                    .filter((feature) => tagFiltering(feature.properties, tags, tagFilterStrategy))
                    .filter((feature) => imageSourceFiltering(feature.properties, activeImageSources)),
            };
        }
        return { type: 'FeatureCollection' as const, features: [] };
    }, [noneActive, platforms, platformsActive, query, tags, tagFilterStrategy, activeImageSources]);
    const setBookmarkedInsightsWrapper = (uuid: string, insert: boolean) => {
        if (insert) {
            setBookmarkedInsights((prevItems) => {
                if (!prevItems.includes(uuid)) {
                    return [...prevItems, uuid];
                } else {
                    return prevItems;
                }
            });
        } else {
            setBookmarkedInsights((prevItems) => {
                return prevItems.filter((item) => item !== uuid);
            });
        }
    };
    const filters: Filters = {
        startDate,
        endDate,
        setStartDate: setStartDateAndUrl,
        setEndDate,
        useDateInterval: selectedView === 'table',
        darkDetectionConfidence,
        setDarkDetectionConfidence: setDarkDetectionConfidenceAndUrl,
        defaultConfidence,
        verifiedActive,
        darkActive,
        unCorrelatedActive,
        windmillsActive,
        platformsActive,
        setVerifiedActive,
        setDarkActive,
        setUnCorrelatedActive,
        setWindmillsActive,
        setPlatformsActive,
        numVerified: matchedVessels?.features.length || 0,
        numDark: darkVessels?.features.length || 0,
        numUncorrelated: unDetectedVessels?.features.length || 0,
        numWindmills: windmills?.features.length || 0,
        numPlatforms: platforms?.features.length || 0,
        tags,
        setTags,
        tagFilterStrategy,
        setTagFilterStrategy,
        openDate,
        setOpenDate,
        openConfidence,
        setOpenConfidence,
        openDetectionType,
        setOpenDetectionType,
        openStaticObjects,
        setOpenStaticObjects,
        openTags,
        setOpenTags,
        query,
        setQuery,
        openImageSources,
        setOpenImageSources,
        sentinel1Active,
        sentinel2Active,
        setSentinel1Active,
        setSentinel2Active,
    };
    const settings: Settings = {
        showProcessingStatus,
        setShowProcessingStatus,
        showSatelliteImage,
        setShowSatelliteImage,
        showEEZ,
        setShowEEZ,
    };
    const view: View = {
        selected,
        setSelected,
        selectedView,
        setSelectedView,
    };
    if (isErrorReport) return <>Error</>;
    if (isLoadingReport) return <>Loading</>;
    if (!isSuccessReport) return <>Not Success</>;
    const linkToActorMode = `/actor/${reportUuid}?startDate=${startDate}&endDate=${endDate}&confidence=${darkDetectionConfidence}`;
    return (
        <OverwatchContext.Provider value={{ report, filters, settings, view, mapView }}>
            <div className="w-screen h-screen">
                <HorizontalProgressBar loading={isAnythingLoading} progress={loadingProgress} />
                <div className="absolute z-20 bottom-20 left-2">
                    <Legend legendData={legendData} />
                </div>
                <div className="absolute z-10 p-2 flex rounded">
                    <LeftBar topChildren={<TopChildren url={linkToActorMode} />} />
                    {/* {selectedView === 'table' && (
                        <div className="bg-background-secondary opacity-90 flex-grow">
                            <VText variant="H1">This is not implemented yet</VText>
                        </div>
                    )} */}
                </div>
                <RMap2
                    activeDate={undefined}
                    availableDates={undefined}
                    selectedFeature={selectedFeature}
                    setSelectedFeature={setSelectedFeatureAndUrl}
                    setHoveredFeature={setHoveredFeature}
                    hoveredFeature={hoveredFeature}
                    darkVessels={darkVesselsFiltered}
                    matchedVessels={matchedVesselsFiltered || { type: 'FeatureCollection', features: [] }}
                    undetectedVessels={unDetectedVesselsFiltered || { type: 'FeatureCollection', features: [] }}
                    windmills={windmillsFiltered || { type: 'FeatureCollection', features: [] }}
                    platforms={platformsFiltered || { type: 'FeatureCollection', features: [] }}
                    aoi={
                        metaData
                            ? { type: 'FeatureCollection', features: metaData.features }
                            : { type: 'FeatureCollection', features: [] }
                    }
                    footprints={[
                        ((sentinel1Active || noSourceActive) && footprintsSentinel1) || {
                            type: 'FeatureCollection',
                            features: [],
                        },
                        ((sentinel2Active || noSourceActive) && footprintsSentinel2) || {
                            type: 'FeatureCollection',
                            features: [],
                        },
                    ]}
                    acquisitions={acquisitions}
                />
                {selectedView === 'map' && (
                    <div className="absolute z-10 top-2 right-2 w-72 bg-background-primary rounded h-[calc(100vh-130px)]">
                        <OverwatchRight2
                            aisPositions={unDetectedVesselsFiltered || { type: 'FeatureCollection', features: [] }}
                            matchedVessels={matchedVesselsFiltered || { type: 'FeatureCollection', features: [] }}
                            darkVessels={darkVesselsFiltered || { type: 'FeatureCollection', features: [] }}
                            windmills={windmillsFiltered || { type: 'FeatureCollection', features: [] }}
                            platforms={platformsFiltered || { type: 'FeatureCollection', features: [] }}
                            selectedFeature={selectedFeature}
                            setSelectedFeature={setSelectedFeatureAndUrl}
                            setHoveredFeature={setHoveredFeature}
                            bookmarkedInsights={bookmarkedInsights}
                            setBookmarkedInsights={setBookmarkedInsightsWrapper}
                            viewedInsights={viewedInsights}
                        />
                    </div>
                )}
            </div>
        </OverwatchContext.Provider>
    );
};
export default Overwatch;
